import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { wrap } from '@popmotion/popcorn';
import Image from 'gatsby-plugin-sanity-image';
import cx from 'classnames';

import useInterval from '@hooks/useInterval';
import { BlockWrapper } from '../blockWrapper';
import { Arrow } from '@svg/arrow';

const slideVars = {
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
};

const Slide = ({
  collectionCard,
  key,
  setPageIndex,
  setIsPlaying,
  layout,
  ...rest
}) => {
  const { figureImage, title, caption, altTag } = rest || {};
  return (
    <motion.div
      initial='hidden'
      className={cx(
        'relative flex flex-col justify-center items-center transform -translate-y-px',
        {
          'col-start-2 col-end-14': layout === 'center' || !layout,
          'col-start-1 col-end-14': layout === 'full',
        },
      )}
      animate='visible'
      exit='hidden'
      key={key}
      variants={slideVars}>
      <div className='w-full relative aspect-w-6 aspect-h-4'>
        <Image
          {...figureImage}
          className='h-full w-full absolute w-full h-full object-cover top-0 left-0'
        />
      </div>

      <div className='flex flex-row justify-between items-center w-full mt-8 gap-x-12'>
        <button
          className='transform rotate-180 cursor-pointer'
          onClick={() => {
            setIsPlaying(false);
            setPageIndex((prev) => prev - 1);
          }}>
          <Arrow white={false} />
        </button>

        <div>
          <p className='text-xs leading-xs md:text-base md:leading-base text-center focus:outline-none'>
            {title}
          </p>
        </div>

        <button
          className='focus:outline-none cursor-pointer'
          onClick={() => {
            setIsPlaying(false);
            setPageIndex((prev) => prev + 1);
          }}>
          <Arrow white={false} />
        </button>
      </div>
    </motion.div>
  );
};

const ImageCarousel = ({ items, layout, blockConfig }) => {
  const [pageIndex, setPageIndex] = useState(0);
  const wrappedIndex = wrap(0, items.length, pageIndex);

  const [delay, setDelay] = useState(8000);

  const [isPlaying, setIsPlaying] = useState(true);
  const [inView, setInView] = useState(true);

  useInterval(
    () => {
      setPageIndex((prev) => prev + 1);
    },
    isPlaying && inView ? delay : null,
  );

  if (pageIndex === items.length) {
    setPageIndex(0);
  }

  useEffect(() => {
    setIsPlaying(true);
  }, [isPlaying]);

  return (
    <BlockWrapper
      {...blockConfig}
      className='relative w-full grid grid-cols-14'>
      <AnimatePresence exitBeforeEnter>
        <Slide
          {...items[wrappedIndex]}
          layout={layout}
          key={pageIndex}
          setPageIndex={setPageIndex}
          setIsPlaying={setIsPlaying}
        />
      </AnimatePresence>
    </BlockWrapper>
  );
};

export default ImageCarousel;
